import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  ConvertRequisitionResponse,
  CreatePurchaseOrderPayload,
  CreatePurchaseRequisitionPayload,
  CreateVendorContactPayload,
  CreateVendorPayload,
  CreateVendorPriceListPayload,
  PaginatedResponse,
  POApprovalRole,
  POApprovalRule,
  PurchaseOrder,
  PurchaseOrderStatus,
  PurchaseRequisition,
  PurchaseRequisitionStatus,
  Receipt,
  ReceivePOPayload,
  ReceivePOResponse,
  UpdatePurchaseOrderPayload,
  UpdatePurchaseRequisitionPayload,
  UpdateVendorPayload,
  Vendor,
  VendorContact,
  VendorPriceList,
  VendorStatus,
  PurchaseOrderLine,
} from '@/lib/types';

const VENDORS_API = '/api/v1';

// ─── Filter Types ──────────────────────────────────────────────────────────

export interface VendorFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: VendorStatus | '';
  preferred?: boolean;
  ordering?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── Vendor Hooks ──────────────────────────────────────────────────────────

export function useVendors(filters: VendorFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.vendors.list(clean),
    queryFn: async (): Promise<PaginatedResponse<Vendor>> => {
      const response = await apiClient.get<PaginatedResponse<Vendor>>(
        `${VENDORS_API}/vendors/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

export function useVendor(id: number) {
  return useQuery({
    queryKey: queryKeys.vendors.detail(id),
    queryFn: async (): Promise<Vendor> => {
      const response = await apiClient.get<Vendor>(`${VENDORS_API}/vendors/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateVendorPayload): Promise<Vendor> => {
      const response = await apiClient.post<Vendor>(`${VENDORS_API}/vendors/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.vendors.all });
    },
  });
}

export function useUpdateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateVendorPayload;
    }): Promise<Vendor> => {
      const response = await apiClient.patch<Vendor>(`${VENDORS_API}/vendors/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.vendors.all });
      qc.invalidateQueries({ queryKey: queryKeys.vendors.detail(variables.id) });
    },
  });
}

export function useDeleteVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${VENDORS_API}/vendors/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.vendors.all });
    },
  });
}

// ─── Vendor Contact Hooks ──────────────────────────────────────────────────

export function useCreateVendorContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateVendorContactPayload): Promise<VendorContact> => {
      const response = await apiClient.post<VendorContact>(
        `${VENDORS_API}/vendor-contacts/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.vendors.detail(variables.vendor) });
      qc.invalidateQueries({ queryKey: queryKeys.vendorContacts.all });
    },
  });
}

// ─── Vendor Price List Hooks ───────────────────────────────────────────────

export function useCreateVendorPriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateVendorPriceListPayload): Promise<VendorPriceList> => {
      const response = await apiClient.post<VendorPriceList>(
        `${VENDORS_API}/vendor-price-lists/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.vendors.detail(variables.vendor) });
      qc.invalidateQueries({ queryKey: queryKeys.vendorPriceLists.all });
    },
  });
}

// ─── Purchase Order Hooks (S17) ────────────────────────────────────────────

export interface PurchaseOrderFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: PurchaseOrderStatus | '';
  vendor?: number | '';
  project?: number | '';
  ordering?: string;
}

export function usePurchaseOrders(filters: PurchaseOrderFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.purchaseOrders.list(clean),
    queryFn: async (): Promise<PaginatedResponse<PurchaseOrder>> => {
      const response = await apiClient.get<PaginatedResponse<PurchaseOrder>>(
        `${VENDORS_API}/purchase-orders/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function usePurchaseOrder(id: number) {
  return useQuery({
    queryKey: queryKeys.purchaseOrders.detail(id),
    queryFn: async (): Promise<PurchaseOrder> => {
      const response = await apiClient.get<PurchaseOrder>(
        `${VENDORS_API}/purchase-orders/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreatePurchaseOrderPayload): Promise<PurchaseOrder> => {
      const response = await apiClient.post<PurchaseOrder>(
        `${VENDORS_API}/purchase-orders/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all });
    },
  });
}

export function useUpdatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdatePurchaseOrderPayload;
    }): Promise<PurchaseOrder> => {
      const response = await apiClient.patch<PurchaseOrder>(
        `${VENDORS_API}/purchase-orders/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all });
      qc.invalidateQueries({
        queryKey: queryKeys.purchaseOrders.detail(variables.id),
      });
    },
  });
}

export function useUpdatePurchaseOrderLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      poId: number;
      data: { tax_rate: string };
    }): Promise<PurchaseOrderLine> => {
      const response = await apiClient.patch<PurchaseOrderLine>(
        `${VENDORS_API}/purchase-order-lines/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all });
      qc.invalidateQueries({
        queryKey: queryKeys.purchaseOrders.detail(variables.poId),
      });
    },
  });
}

export function useDeletePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${VENDORS_API}/purchase-orders/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all });
    },
  });
}

function invalidatePO(qc: ReturnType<typeof useQueryClient>, id: number) {
  qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all });
  qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(id) });
}

export function useSubmitPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<PurchaseOrder> => {
      const response = await apiClient.post<PurchaseOrder>(
        `${VENDORS_API}/purchase-orders/${id}/submit/`
      );
      return response.data;
    },
    onSuccess: (_, id) => invalidatePO(qc, id),
  });
}

export function useApprovePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      role,
      notes,
    }: {
      id: number;
      role: POApprovalRole;
      notes?: string;
    }): Promise<PurchaseOrder> => {
      const response = await apiClient.post<PurchaseOrder>(
        `${VENDORS_API}/purchase-orders/${id}/approve/`,
        { role, notes }
      );
      return response.data;
    },
    onSuccess: (_, variables) => invalidatePO(qc, variables.id),
  });
}

export function useRejectPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      role,
      notes,
    }: {
      id: number;
      role: POApprovalRole;
      notes?: string;
    }): Promise<PurchaseOrder> => {
      const response = await apiClient.post<PurchaseOrder>(
        `${VENDORS_API}/purchase-orders/${id}/reject/`,
        { role, notes }
      );
      return response.data;
    },
    onSuccess: (_, variables) => invalidatePO(qc, variables.id),
  });
}

export function useSendPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<PurchaseOrder> => {
      const response = await apiClient.post<PurchaseOrder>(
        `${VENDORS_API}/purchase-orders/${id}/send/`
      );
      return response.data;
    },
    onSuccess: (_, id) => invalidatePO(qc, id),
  });
}

export function useCancelPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<PurchaseOrder> => {
      const response = await apiClient.post<PurchaseOrder>(
        `${VENDORS_API}/purchase-orders/${id}/cancel/`
      );
      return response.data;
    },
    onSuccess: (_, id) => invalidatePO(qc, id),
  });
}

export function usePurchaseOrderPdfUrl(id: number): string {
  return `${VENDORS_API}/purchase-orders/${id}/pdf/`;
}

// ─── PO Approval Rules ────────────────────────────────────────────────────

export function usePOApprovalRules() {
  return useQuery({
    queryKey: queryKeys.poApprovalRules.list({}),
    queryFn: async (): Promise<POApprovalRule[]> => {
      const response = await apiClient.get<
        PaginatedResponse<POApprovalRule> | POApprovalRule[]
      >(`${VENDORS_API}/po-approval-rules/`);
      const data = response.data;
      if (Array.isArray(data)) return data;
      return data.results ?? [];
    },
  });
}

// ─── Purchase Requisitions ────────────────────────────────────────────────

export interface PurchaseRequisitionFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: PurchaseRequisitionStatus | '';
  project?: number | '';
  mine?: boolean | '';
}

export function usePurchaseRequisitions(
  filters: PurchaseRequisitionFilters = {},
  enabled = true
) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.purchaseRequisitions.list(clean),
    queryFn: async (): Promise<PaginatedResponse<PurchaseRequisition>> => {
      const response = await apiClient.get<
        PaginatedResponse<PurchaseRequisition>
      >(`${VENDORS_API}/purchase-requisitions/`, { params: clean });
      return response.data;
    },
    enabled,
  });
}

/** Count of requisitions awaiting approval — drives the nav badge. */
export function usePendingRequisitionCount(enabled = true) {
  const query = usePurchaseRequisitions(
    { status: 'submitted', page_size: 1 },
    enabled
  );
  return { ...query, count: query.data?.count ?? 0 };
}

export function usePurchaseRequisition(id: number) {
  return useQuery({
    queryKey: queryKeys.purchaseRequisitions.detail(id),
    queryFn: async (): Promise<PurchaseRequisition> => {
      const response = await apiClient.get<PurchaseRequisition>(
        `${VENDORS_API}/purchase-requisitions/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

function invalidateRequisition(qc: ReturnType<typeof useQueryClient>, id?: number) {
  qc.invalidateQueries({ queryKey: queryKeys.purchaseRequisitions.all });
  if (id) qc.invalidateQueries({ queryKey: queryKeys.purchaseRequisitions.detail(id) });
}

export function useCreatePurchaseRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: CreatePurchaseRequisitionPayload
    ): Promise<PurchaseRequisition> => {
      const response = await apiClient.post<PurchaseRequisition>(
        `${VENDORS_API}/purchase-requisitions/`,
        data
      );
      return response.data;
    },
    onSuccess: () => invalidateRequisition(qc),
  });
}

export function useUpdatePurchaseRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdatePurchaseRequisitionPayload;
    }): Promise<PurchaseRequisition> => {
      const response = await apiClient.patch<PurchaseRequisition>(
        `${VENDORS_API}/purchase-requisitions/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => invalidateRequisition(qc, variables.id),
  });
}

export function useDeletePurchaseRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${VENDORS_API}/purchase-requisitions/${id}/`);
    },
    onSuccess: () => invalidateRequisition(qc),
  });
}

export function useSubmitPurchaseRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<PurchaseRequisition> => {
      const response = await apiClient.post<PurchaseRequisition>(
        `${VENDORS_API}/purchase-requisitions/${id}/submit/`
      );
      return response.data;
    },
    onSuccess: (_, id) => invalidateRequisition(qc, id),
  });
}

export function useApprovePurchaseRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      comment,
      confirm_self_approval,
    }: {
      id: number;
      comment?: string;
      confirm_self_approval?: boolean;
    }): Promise<PurchaseRequisition> => {
      const response = await apiClient.post<PurchaseRequisition>(
        `${VENDORS_API}/purchase-requisitions/${id}/approve/`,
        {
          comment: comment ?? '',
          ...(confirm_self_approval ? { confirm_self_approval: true } : {}),
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => invalidateRequisition(qc, variables.id),
  });
}

export function useRejectPurchaseRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      comment,
    }: {
      id: number;
      comment: string;
    }): Promise<PurchaseRequisition> => {
      const response = await apiClient.post<PurchaseRequisition>(
        `${VENDORS_API}/purchase-requisitions/${id}/reject/`,
        { comment }
      );
      return response.data;
    },
    onSuccess: (_, variables) => invalidateRequisition(qc, variables.id),
  });
}

export function useConvertPurchaseRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      vendor,
      po_number,
    }: {
      id: number;
      vendor: number;
      po_number?: string;
    }): Promise<ConvertRequisitionResponse> => {
      const response = await apiClient.post<ConvertRequisitionResponse>(
        `${VENDORS_API}/purchase-requisitions/${id}/convert/`,
        { vendor, po_number: po_number ?? '' }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateRequisition(qc, variables.id);
      qc.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all });
    },
  });
}

// ─── Receipts (S18) ──────────────────────────────────────────────────────

export function useReceivePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: ReceivePOPayload;
    }): Promise<ReceivePOResponse> => {
      const response = await apiClient.post<ReceivePOResponse>(
        `${VENDORS_API}/purchase-orders/${id}/receive/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidatePO(qc, variables.id);
      qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
    },
  });
}

export function usePOReceipts(poId: number) {
  return useQuery({
    queryKey: [...queryKeys.purchaseOrders.detail(poId), 'receipts'] as const,
    queryFn: async (): Promise<Receipt[]> => {
      const response = await apiClient.get<Receipt[]>(
        `${VENDORS_API}/purchase-orders/${poId}/receipts/`
      );
      return response.data;
    },
    enabled: poId > 0,
  });
}
